import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { EditorUtils } from "@progress/kendo-react-editor";
import { Input } from "@progress/kendo-react-inputs";
import { updatesIsCollabCentralOpened } from "../../actions/common";
import { createWorkflowTask } from "../../components/TaskSidebar/sidebar.helper";
import { handleCloseSidebar } from "../../components/Tasks/tasks.common";
import { openSidebar } from "../../components/Tasks/tasks.service";
import { icon, number, quote, tooltip } from "../../config";
import { getTaskUrl } from "../../helper/common";
import { copyTextToClipboard } from "../../utils/common";
import { navigateToBookmark } from "../editorTools/bookmarkUtils";
import TaskDetails from "./TaskDetails";
import { Tooltip } from "@progress/kendo-react-tooltip";

/**
 * Hyper Link Popup  for Editor
 * PC KendoEditor
 * @author Shivam Mishra
 */
const HyperLinkPopup = ({ url, selectedLink, setVisible, visible, editorRef, taskInfo, editableRef, hashPopupVisible, setHashPopupVisible }) => {
  const dispatch = useDispatch();
  const { mode } = useSelector((state) => state.taskSidebar.task);
  const [editHref, setEditHref] = React.useState(false);
  const [urlInput, setUrlInput] = React.useState("");
  const hoveredTaskChipURL = taskInfo?.Id && `${window.location.origin}${getTaskUrl(taskInfo?.Id)}`;

  const history = useHistory();

  const specialCharacters = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };

  /**
   * useEffect hook to handle initialization and updates related to the URL input.
   * @param {string} url - The URL input to be processed.
   * @returns {void}
   * @author Shivam Mishra
   */
  useEffect(() => {
    setUrlInput(url);
    setEditHref(false);
  }, [url]);

  /**
   * useCallback function to toggle the visibility of a dialog.
   * @param {boolean} visible - The current visibility state of the dialog.
   * @returns {Function} - Returns the function to toggle the visibility of the dialog.
   * @author Shivam Mishra
   */
  const toggleDialog = useCallback(() => {
    setVisible(false);
  }, []);

  /**
   * useCallback function to handle change event for URL input.
   * @param {ChangeEvent} e - The change event containing the new value of the URL input.
   * @returns {Function} - Returns the function to handle the change event for URL input.
   * @author Shivam Mishra
   */
  const handleChange = useCallback((e) => {
    setUrlInput(e.target.value);
  }, []);

  /**
   * useCallback function to handle updating a link.
   * @returns {Function} - Returns the function to handle updating a link.
   * @author Shivam Mishra
   */
  const handleUpdateLink = useCallback(() => {
    if (selectedLink) {
      const view = editorRef.current.view;
      const viewHTML = EditorUtils.getHtml(view.state);
      const updatedContent = viewHTML.replaceAll(
        selectedLink.getAttribute("href").replaceAll(/[&<>"']/g, (match) => specialCharacters[match]),
        urlInput
      );
      EditorUtils.setHtml(view, updatedContent);
      toggleDialog();
    }
  }, [selectedLink, visible, urlInput]);

  /**
   * useCallback function to toggle the editability of a link.
   * @returns {Function} - Returns the function to toggle the editability of a link.
   * @author Shivam Mishra
   */
  const handleEditable = useCallback(() => {
    setEditHref(!editHref);
  }, [editHref]);

  /**
   * useCallback function to handle unlinking a link.
   * @returns {Function} - Returns the function to handle unlinking a link.
   * @author Shivam Mishra
   */
  const handleUnlink = useCallback(() => {
    if (selectedLink) {
      const text = document.createTextNode(selectedLink.textContent);
      selectedLink.parentNode.replaceChild(text, selectedLink);
      toggleDialog();
    }
  }, [selectedLink, visible]);

  /**
   * useCallback function to copy a URL to clipboard.
   * @returns {Function} - Returns the function to copy a URL to clipboard.
   * @author Shivam Mishra
   */
  const copyToClipboard = useCallback(() => {
    const copiedLink = url ? url : hoveredTaskChipURL;
    copyTextToClipboard(copiedLink, quote.HYPER_LINK_COPIED_TO_CLIPBOARD);
  }, [url]);

  /**
   * Handles anchor click events, triggering specific actions based on URL parameters.
   * @param {Event} event - The click event triggered by the anchor element.
   * @author Shivam Mishra
   */
  function handleAnchorClick(event) {
    const { target } = event;
    const view = editorRef?.current?.view;
    if (target.localName === "a") {
      const currentHostname = window.location.hostname;
      const urlPath = target.getAttribute("href");
      const url = new URL(urlPath);
      const isCmsRoute = /^\/cms\/[^/]+$/.test(url.pathname); // matches /cms/{id}
      const isInternal = window.location.hostname === url.hostname;
      const params = new URLSearchParams(url.search);
      const createTask = params.get("createTask");
      const fId = params.get("fId");
      const pId = params.get("pId");
      const tId = params.get("tId");
      // Open /cms/:id in new tab
      if (isCmsRoute) {
        const hash = url.hash.slice(1);
        if (view && hash) {
          // Check if current URL matches the href (excluding hash)
          const currentUrl = new URL(window.location.href);
          const hrefUrl = new URL(urlPath, window.location.origin);
          // Remove hash from both URLs for comparison
          currentUrl.hash = "";
          hrefUrl.hash = "";
          // Only navigate to bookmark if URLs match (excluding hash)
          if (currentUrl.href === hrefUrl.href) {
            event.preventDefault();
            navigateToBookmark(view, hash);
            return;
          }
        }
        // Allow default behavior if target=_blank already exists
        // OR explicitly open in new tab here:
        event.preventDefault();
        window.open(url.href, "_blank");
        return;
      }
      dispatch(updatesIsCollabCentralOpened(false));
      if (currentHostname === url.hostname) {
        event.preventDefault();
        if (fId && pId && createTask) {
          createWorkflowTask(fId, pId);
        } else if (tId) {
          openSidebar({ id: tId });
        } else {
          handleCloseSidebar(dispatch, { isNewTask: false });
          history.push(url.pathname + url.search);
        }
      }
    }
  }

  const showIconRow = mode ? mode === number.TWO : true;
  const getEditIconKey = () => (showIconRow ? (editHref ? "updateLink" : "editLink") : "empty");
  const getUnlinkIconKey = () => (showIconRow ? "unlink" : "empty");

  const editIconComponent = {
    empty: <div></div>,
    updateLink: (
      <Tooltip position='bottom' parentTitle={true}>
        <div onClick={handleUpdateLink} className='d-flex justify-content-center align-items-center mr-2 cursor-pointer d-block editor-popup-task-icon'>
          {icon.DONE_OUTLINED}
        </div>
      </Tooltip>
    ),
    editLink: (
      <Tooltip position='bottom' parentTitle={true}>
        <div onClick={handleEditable} className='d-flex justify-content-center align-items-center mr-2 cursor-pointer d-block editor-popup-task-icon' title={tooltip.EDIT_LINK}>
          {icon.EDIT_ICON}
        </div>
      </Tooltip>
    ),
  };
  const unlinkIconComponent = {
    empty: <div></div>,
    unlink: (
      <Tooltip position='bottom' parentTitle={true}>
        <div onClick={handleUnlink} className='d-flex justify-content-center align-items-center mr-2 cursor-pointer d-block task-icons-link editor-popup-task-icon' title={tooltip.REMOVE_LINK}>
          {icon.DELINK_TASK}
        </div>
      </Tooltip>
    ),
  };

  return (
    <>
      {!hashPopupVisible && (
        <div className='row'>
          <div className='col-md-12 '>
            <div className='d-flex justify-content-center align-items-center border-bottom pb-2'>
              {(url || taskInfo) && (
                <>
                  <div className='mr-2 task-icons-link'>{icon.PRIVACY_PUBLIC}</div>
                  {editHref ? (
                    <Input className='form-control mr-2' value={urlInput} onChange={handleChange} />
                  ) : (
                    <div id='editor-popup-hovered-button' className='mr-2 cursor-pointer editor-popup-link-url width-200 text-truncate'>
                      <a target='_blank' rel='noreferrer' href={url || hoveredTaskChipURL} onClick={handleAnchorClick}>
                        {url || hoveredTaskChipURL}
                      </a>
                    </div>
                  )}
                </>
              )}
              {editableRef.current && url && (
                <div className='d-flex justify-content-center align-items-center task-icons-link'>
                  {editIconComponent[getEditIconKey()]}
                  {unlinkIconComponent[getUnlinkIconKey()]}
                </div>
              )}
              {(url || taskInfo) && (
                <Tooltip position='bottom' parentTitle={true}>
                <div onClick={copyToClipboard} className='d-flex justify-content-center align-items-center cursor-pointer d-block mr-4 task-icons-link editor-popup-task-icon' id='copy-hyperlink-btn' title={tooltip.COPY_TASK_LINK}>
                  {icon.COPY_CONTENT}
                </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )}
      {taskInfo && (
        <TaskDetails // Selected Task Details
          taskInfo={taskInfo}
          hashPopupVisible={hashPopupVisible}
          url={url}
          copyToClipboard={copyToClipboard}
          handleAnchorClick={handleAnchorClick}
          setHashPopupVisible={setHashPopupVisible}
        />
      )}
    </>
  );
};

export default React.memo(HyperLinkPopup);
